// src/lib/gangrun.js
// ───────────────────────────────────────────────────────────────────────────
// 🧩 Gang Run / Combos (PLAN-GANG-RUN) — el gang es un JOB de producción PADRE
// con N líneas-componente, cada una ligada a la ORDEN de su cliente. Una corrida
// de máquina, varias órdenes. Modo PTO (producir-a-orden) ahora; cables para PTI
// (producir-a-stock) dejados en cada línea (fulfillmentMode/produced/overs).
//
// Vive en la colección `jobs` (type:'GangRun') para aparecer en el Dashboard como
// "un job más". Reusa la numeración consecutiva counters/jobs (id J-N).
// ───────────────────────────────────────────────────────────────────────────
import { doc, setDoc, updateDoc, runTransaction, serverTimestamp } from "firebase/firestore";

/** Normaliza una línea del gang (componente). PTI cables: fulfillmentMode/produced/overs. PURO. */
export function buildGangLine(l) {
  const ordered = Number(l.orderedQty) || 0;
  const ups = Number(l.ups) || 1;
  return {
    sourceJobId: l.sourceJobId || null,       // la orden/trabajo del cliente que esta línea cumple
    sourceJobNumber: l.sourceJobNumber || null,
    clientId: l.clientId || null,
    clientName: l.clientName || "",
    skuRef: l.skuRef || null,
    sku: l.sku || "",
    pieceCode: (l.pieceCode || "").trim(),
    pieceName: l.pieceName || "",
    orderedQty: ordered,
    ups,
    // ── Cables/cimientos PTI (no usados en PTO; listos para producir-a-stock) ──
    fulfillmentMode: l.fulfillmentMode || "PTO", // 'PTO' = a orden | 'PTI' = a stock
    produced: Number(l.produced) || 0,           // se llenará al registrar producción (Fase B)
    overs: Number(l.overs) || 0,                 // sobrantes (PTI: stock; PTO: merma)
  };
}

/** Suma de cantidades pedidas (para sugerir hojas). PURO. */
export function totalOrdered(lines) {
  return (lines || []).reduce((a, l) => a + (Number(l.orderedQty) || 0), 0);
}

/**
 * Sugiere las hojas a correr (balanceo simple): la línea más exigente manda.
 * hojas = max_i( ceil(orderedQty_i / ups_i) ). PURO (testeable).
 */
export function suggestSheets(lines) {
  let s = 0;
  for (const l of lines || []) {
    const ups = Number(l.ups) || 0;
    const q = Number(l.orderedQty) || 0;
    if (ups > 0 && q > 0) s = Math.max(s, Math.ceil(q / ups));
  }
  return s;
}

/**
 * Crea un Gang Run como job (type:'GangRun'). Devuelve el jobId (J-N).
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} payload { jobName?, materialId?, materialName?, sheet, lines[], sheetsToRun? }
 * @param {object} user
 */
export async function createGangRun(db, payload, user) {
  const counterRef = doc(db, "counters", "jobs");
  const jobNumber = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    if (!snap.exists()) throw new Error("El contador de trabajos (counters/jobs) no existe.");
    const next = (snap.data().lastNumber || 0) + 1;
    tx.update(counterRef, { lastNumber: next });
    return next;
  });
  const jobId = `J-${jobNumber}`;
  const lines = (payload.lines || []).map(buildGangLine);
  await setDoc(doc(db, "jobs", jobId), {
    jobName: payload.jobName || `Gang Run ${jobId}`,
    type: "GangRun",
    clientId: null, // multi-cliente
    materialId: payload.materialId || null,
    materialName: payload.materialName || "",
    sheet: payload.sheet || { width: null, height: null, unit: "in" },
    gangLines: lines,
    sheetsToRun: Number(payload.sheetsToRun) || suggestSheets(lines),
    quantity: 1,
    components: [],
    assignedRoles: [],
    documents: [],
    status: { stage: "NUEVO", phase: "PENDIENTE" },
    createdVia: "gang",
    jobNumber,
    createdAt: serverTimestamp(),
    createdBy: user?.uid || null,
  });
  return jobId;
}

/** Reemplaza las líneas del gang (editar en JobView). */
export async function setGangLines(db, jobId, lines) {
  const norm = (lines || []).map(buildGangLine);
  await updateDoc(doc(db, "jobs", jobId), { gangLines: norm, sheetsToRun: suggestSheets(norm) });
}
